import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaUser, FaEye, FaBullseye, FaQuoteLeft, FaAward, FaHeart } from "react-icons/fa";
import vision from "../assets/vision.png";
import mission from "../assets/mission.png";

const FounderSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('founder-section');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 50,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="founder-section" className="py-20 bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-rose-200/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-amber-200/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
      <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-orange-200/15 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="inline-flex items-center space-x-2 bg-rose-100 text-rose-700 px-6 py-2 rounded-full text-sm font-semibold mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isVisible ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <FaUser />
            <span>Leadership</span>
          </motion.div>

          <h2 className="text-4xl lg:text-6xl font-bold font-platypi text-gray-800 mb-6">
            <span className="gradient-text bg-gradient-to-r from-rose-600 to-orange-600 bg-clip-text text-transparent">
              Meet Our
            </span>
            <br />
            <span className="text-gray-700">Prime Mover</span>
          </h2>
        </motion.div>

        {/* Founder Profile */}
        <motion.div
          className="max-w-4xl mx-auto mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 lg:p-12 shadow-soft text-center">
            <div className="mb-6">
              <h3 className="text-3xl lg:text-4xl font-bold font-platypi text-gray-800 mb-2">
                Rohini Kharade
              </h3>
              <p className="text-xl text-rose-600 font-semibold mb-4">Prime Mover</p>
              <div className="flex justify-center items-center space-x-2 text-amber-500 mb-6">
                <FaAward />
                <span className="text-gray-600 font-medium">14 Years of Excellence in Education</span>
              </div>
            </div>

            <div className="relative">
              <FaQuoteLeft className="absolute -top-4 -left-4 text-3xl text-rose-300" />
              <p className="text-lg lg:text-xl text-gray-700 leading-relaxed italic max-w-3xl mx-auto">
                "My passion lies in nurturing young minds and creating a safe, engaging environment where every child can flourish.
                Under my leadership, we strive to provide the highest quality of care and education, ensuring each child receives
                the individual attention they deserve to reach their full potential."
              </p>
            </div>
          </div>
        </motion.div>

        {/* Vision & Mission */}
        <motion.div
          className="grid lg:grid-cols-2 gap-8 lg:gap-12"
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          {/* Vision Card */}
          <motion.div variants={cardVariants} className="group">
            <div className="modern-card bg-gradient-to-br from-orange-50 to-amber-50 p-8 h-full relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
                <div className="w-full h-full bg-gradient-to-br from-orange-400 to-amber-400 rounded-full transform rotate-45"></div>
              </div>

              {/* Header */}
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-amber-500 rounded-2xl flex items-center justify-center text-white shadow-medium group-hover:shadow-strong transition-all duration-300">
                  <FaEye size={24} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold font-platypi text-gray-800">Our Vision</h3>
                  <p className="text-gray-600">Shaping Tomorrow's Leaders</p>
                </div>
              </div>

              {/* Vision Image */}
              <div className="flex justify-center mb-6">
                <motion.div
                  className="relative"
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-amber-400 rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
                  <img
                    src={vision}
                    alt="Our Vision"
                    className="relative z-10 w-32 h-32 lg:w-40 lg:h-40 rounded-full shadow-lg object-cover"
                  />
                </motion.div>
              </div>

              {/* Vision Content */}
              <p className="text-gray-700 leading-relaxed">
                Our Vision at Smart Educare Centre and Bagless Homeschooling is to redefine education by creating
                a future where learning is joyful, meaningful, and free from unnecessary burdens. We envision a world
                where every child discovers their unique potential through innovative, stress-free educational approaches
                that foster creativity, critical thinking, and emotional intelligence.
              </p>

              {/* Learn More Button */}
              <motion.button
                className="w-full bg-gradient-to-r from-orange-400 to-amber-500 text-white py-3 px-6 rounded-xl font-semibold shadow-medium hover:shadow-strong transition-all duration-300 mt-6 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Learn More
              </motion.button>
            </div>
          </motion.div>

          {/* Mission Card */}
          <motion.div variants={cardVariants} className="group">
            <div className="modern-card bg-gradient-to-br from-teal-50 to-cyan-50 p-8 h-full relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
                <div className="w-full h-full bg-gradient-to-br from-teal-400 to-cyan-400 rounded-full transform rotate-45"></div>
              </div>

              {/* Header */}
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-2xl flex items-center justify-center text-white shadow-medium group-hover:shadow-strong transition-all duration-300">
                  <FaBullseye size={24} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold font-platypi text-gray-800">Our Mission</h3>
                  <p className="text-gray-600">Nurturing Every Child</p>
                </div>
              </div>

              {/* Mission Image */}
              <div className="flex justify-center mb-6">
                <motion.div
                  className="relative"
                  whileHover={{ scale: 1.05, rotate: -2 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-cyan-400 rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
                  <img
                    src={mission}
                    alt="Our Mission"
                    className="relative z-10 w-32 h-32 lg:w-40 lg:h-40 rounded-full shadow-lg object-cover"
                  />
                </motion.div>
              </div>

              {/* Mission Content */}
              <p className="text-gray-700 leading-relaxed">
                At Smart Educare Centre and The Fun Hub Bagless Homeschooling, our mission is to nurture a love for
                learning through a joyful, stress-free and holistic educational experience. We are committed to
                providing personalized attention, fostering creativity, and building strong foundations that prepare
                children for lifelong success while maintaining their natural curiosity and enthusiasm for learning.
              </p>

              {/* Learn More Button */}
              <motion.button
                className="w-full bg-gradient-to-r from-teal-400 to-cyan-500 text-white py-3 px-6 rounded-xl font-semibold shadow-medium hover:shadow-strong transition-all duration-300 mt-6 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Learn More
              </motion.button>
            </div>
          </motion.div>
        </motion.div>

        {/* Bottom CTA */}
        {/* <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-soft max-w-2xl mx-auto">
            <FaHeart className="text-4xl text-rose-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Join Our Educational Family
            </h3>
            <p className="text-gray-600 mb-6">
              Experience the difference that passionate, dedicated leadership makes in your child's educational journey.
            </p>
            <motion.button
              className="bg-gradient-to-r from-rose-500 to-orange-500 text-white px-8 py-4 rounded-2xl font-semibold text-lg shadow-medium hover:shadow-strong transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Started Today
            </motion.button>
          </div>
        </motion.div> */}
      </div>
    </section>
  );
};

export default FounderSection;
